import { TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { ForceFieldValues } from './params';

/**
 * The text as an electric charge, with particles tracing their orbits.
 *
 * The force is read from the distance field: its gradient gives the direction away from
 * the nearest edge, and the magnitude falls off with distance by the chosen exponent.
 * A purely radial force just makes particles fall in and stop, so a tangential term is
 * added — that is what turns the trajectories into orbits and produces the shells that
 * outline the letterforms.
 *
 * Trails go into an accumulation buffer, bucketed by colour like the flow field, for the
 * same reason: millions of segments cannot each carry a style change.
 */
const COLOR_BUCKETS = 40;

export interface ForceFieldState {
  buffer: OffscreenCanvas | HTMLCanvasElement;
  bctx: Ctx2D;
  x: Float32Array;
  y: Float32Array;
  vx: Float32Array;
  vy: Float32Array;
  tint: Float32Array;
  count: number;
  attractorX: Float32Array;
  attractorY: Float32Array;
  styles: string[];
  buckets: { coords: Float32Array; n: number }[];
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
  segments: number;
}

function values(ctx: TechniqueContext): ForceFieldValues {
  return ctx.values as unknown as ForceFieldValues;
}

export function init(ctx: TechniqueContext): ForceFieldState {
  const v = values(ctx);
  const count = Math.max(1, v.particles);
  const buffer = createCanvas(ctx.width, ctx.height);
  const bctx = get2d(buffer);
  bctx.lineCap = 'round';
  bctx.lineWidth = v.lineWidth;

  const palette = resolvePalette(v.palette, ctx.rng.fork(1));
  const state: ForceFieldState = {
    buffer,
    bctx,
    x: new Float32Array(count),
    y: new Float32Array(count),
    vx: new Float32Array(count),
    vy: new Float32Array(count),
    tint: new Float32Array(count),
    count,
    attractorX: new Float32Array(v.attractors),
    attractorY: new Float32Array(v.attractors),
    styles: Array.from({ length: COLOR_BUCKETS }, (_, i) =>
      v.colorMode === 'solid' ? palette.stop(0, v.opacity) : palette.at(i / (COLOR_BUCKETS - 1), v.opacity),
    ),
    buckets: Array.from({ length: COLOR_BUCKETS }, () => ({ coords: new Float32Array(512), n: 0 })),
    palette,
    stepsDone: 0,
    targetSteps: v.steps,
    segments: 0,
  };

  for (let i = 0; i < v.attractors; i++) {
    state.attractorX[i] = ctx.rng.next() * ctx.width;
    state.attractorY[i] = ctx.rng.next() * ctx.height;
  }

  for (let i = 0; i < count; i++) {
    state.x[i] = ctx.rng.next() * ctx.width;
    state.y[i] = ctx.rng.next() * ctx.height;
    const angle = ctx.rng.next() * TAU;
    const speed = ctx.rng.next() * v.maxSpeed * 0.4;
    state.vx[i] = Math.cos(angle) * speed;
    state.vy[i] = Math.sin(angle) * speed;
    state.tint[i] = ctx.rng.next();
  }

  return state;
}

function pushSegment(state: ForceFieldState, bucket: number, x0: number, y0: number, x1: number, y1: number): void {
  const b = state.buckets[bucket];
  if (b.n + 4 > b.coords.length) {
    const grown = new Float32Array(b.coords.length * 2);
    grown.set(b.coords);
    b.coords = grown;
  }
  b.coords[b.n++] = x0;
  b.coords[b.n++] = y0;
  b.coords[b.n++] = x1;
  b.coords[b.n++] = y1;
  state.segments++;
}

function flush(state: ForceFieldState): void {
  const bctx = state.bctx;
  for (let i = 0; i < state.buckets.length; i++) {
    const bucket = state.buckets[i];
    if (bucket.n === 0) continue;
    bctx.strokeStyle = state.styles[i];
    bctx.beginPath();
    const c = bucket.coords;
    for (let k = 0; k < bucket.n; k += 4) {
      bctx.moveTo(c[k], c[k + 1]);
      bctx.lineTo(c[k + 2], c[k + 3]);
    }
    bctx.stroke();
    bucket.n = 0;
  }
}

export function step(state: ForceFieldState, _dt: number, ctx: TechniqueContext): ForceFieldState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);
  const perFrame = Math.max(1, Math.round(6 * clamp01(ctx.quality)));
  const reach = Math.max(1, ctx.field.maxOutside);
  const keep = 1 - v.damping;

  for (let f = 0; f < perFrame && state.stepsDone < state.targetSteps; f++) {
    const timeT = state.stepsDone / Math.max(1, state.targetSteps - 1);

    for (let i = 0; i < state.count; i++) {
      const px = state.x[i];
      const py = state.y[i];

      const signed = ctx.field.distance(px, py);
      const [gx, gy] = ctx.field.gradient(px, py);

      let ax = 0;
      let ay = 0;

      if (gx !== 0 || gy !== 0) {
        // Inverse-power falloff, clamped near the surface so the force stays finite.
        const d = Math.max(4, Math.abs(signed));
        const magnitude = v.charge / d ** v.falloff * 40;
        ax += gx * magnitude;
        ay += gy * magnitude;
        // Tangential term: perpendicular to the gradient.
        const orbit = (v.orbitSpeed / d ** (v.falloff * 0.6)) * 24;
        ax += -gy * orbit;
        ay += gx * orbit;
      }

      for (let a = 0; a < state.attractorX.length; a++) {
        const dx = state.attractorX[a] - px;
        const dy = state.attractorY[a] - py;
        const d = Math.max(8, Math.hypot(dx, dy));
        const magnitude = (v.attractorStrength / d ** v.falloff) * 120;
        ax += (dx / d) * magnitude;
        ay += (dy / d) * magnitude;
      }

      let nvx = (state.vx[i] + ax) * keep;
      let nvy = (state.vy[i] + ay) * keep;
      const speed = Math.hypot(nvx, nvy);
      if (speed > v.maxSpeed) {
        nvx = (nvx / speed) * v.maxSpeed;
        nvy = (nvy / speed) * v.maxSpeed;
      }

      let nx = px + nvx;
      let ny = py + nvy;
      let jumped = false;
      if (nx < 0) {
        nx += ctx.width;
        jumped = true;
      } else if (nx >= ctx.width) {
        nx -= ctx.width;
        jumped = true;
      }
      if (ny < 0) {
        ny += ctx.height;
        jumped = true;
      } else if (ny >= ctx.height) {
        ny -= ctx.height;
        jumped = true;
      }

      if (!jumped) {
        let t: number;
        switch (v.colorMode) {
          case 'speed':
            t = clamp01(Math.hypot(nvx, nvy) / v.maxSpeed);
            break;
          case 'distance':
            t = clamp01(Math.abs(signed) / reach);
            break;
          case 'time':
            t = timeT;
            break;
          case 'random':
            t = state.tint[i];
            break;
          default:
            t = 0;
        }
        pushSegment(state, Math.min(COLOR_BUCKETS - 1, (t * COLOR_BUCKETS) | 0), px, py, nx, ny);
      }

      state.x[i] = nx;
      state.y[i] = ny;
      state.vx[i] = nvx;
      state.vy[i] = nvy;
    }

    flush(state);
    state.stepsDone++;
  }

  return state;
}

export function isComplete(state: ForceFieldState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: ForceFieldState, target: RenderTarget): void {
  target.ctx.drawImage(state.buffer as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: ForceFieldState): number {
  return state.segments;
}
